import { memo, useCallback } from "react";
import { useWatch } from "react-hook-form";
import type { FilterRangeData } from "@/api/filter-ranges";
import { HistogramSlider } from "@/components/base/histogram-slider";
import { SimpleRangeSlider } from "@/components/base/simple-range-slider";
import { LabelWithTooltip } from "@/components/filters/shared";
import type { useFilters } from "@/providers/filter-provider";

type GmvRangeFilterProps = {
  form: ReturnType<typeof useFilters>["form"];
  min?: number;
  max?: number;
  rangeData?: FilterRangeData;
  label?: string;
  tooltip?: string;
  description?: string;
  showHistogram?: boolean;
};

export const GmvRangeFilter = memo(
  ({
    form,
    min,
    max,
    rangeData,
    label = "GMV",
    tooltip = "GMV",
    description = "Gross Merchandise Value - Total sales revenue generated.",
    showHistogram = false,
  }: GmvRangeFilterProps) => {
    // Support both direct min/max props and rangeData for backwards compatibility
    const rangeMin = min ?? rangeData?.min ?? 0;
    const rangeMax = max ?? rangeData?.max ?? 1200;

    // Use useWatch for efficient subscriptions to specific fields
    const minGmv = useWatch({ control: form.control, name: "minGmv" });
    const maxGmv = useWatch({ control: form.control, name: "maxGmv" });

    const minValue = Number(minGmv) || rangeMin;
    const maxValue = Number(maxGmv) || rangeMax;

    const handleMinChange = useCallback(
      (value: number) =>
        form.setValue("minGmv", value === rangeMin ? undefined : String(value), {
          shouldDirty: true,
          shouldTouch: true,
        }),
      [form, rangeMin],
    );

    const handleMaxChange = useCallback(
      (value: number) =>
        form.setValue("maxGmv", value === rangeMax ? undefined : String(value), {
          shouldDirty: true,
          shouldTouch: true,
        }),
      [form, rangeMax],
    );

    return (
      <div className="flex flex-col gap-2">
        <LabelWithTooltip label={label} tooltip={tooltip} description={description} />
        {showHistogram && rangeData?.histogram ? (
          <HistogramSlider
            min={rangeMin}
            max={rangeMax}
            minValue={Math.min(minValue, maxValue)}
            maxValue={Math.max(maxValue, minValue)}
            onMinChange={handleMinChange}
            onMaxChange={handleMaxChange}
            histogramData={rangeData.histogram}
            format="currency"
          />
        ) : (
          <SimpleRangeSlider
            min={rangeMin}
            max={rangeMax}
            minValue={Math.min(minValue, maxValue)}
            maxValue={Math.max(maxValue, minValue)}
            onMinChange={handleMinChange}
            onMaxChange={handleMaxChange}
            format="currency"
          />
        )}
      </div>
    );
  },
);
